/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import * as drive from "../../../../api/drive";

const TEXT_BUTTON_SX = { color: "text.color", textTransform: "none" };
const SWATCHES = ["#5C6BC0", "#E53935", "#43A047", "#FB8C00", "#8E24AA", "#00897B"];

/**
 * Every label in the org, click to toggle onto this file. Creating a new
 * label happens inline here rather than in a separate admin screen — WorkDrive
 * lets any member coin a label, so this does too.
 */
const LabelsDialog = ({ open, file, onClose }) => {
  const [allLabels, setAllLabels] = useState(null);
  const [assigned, setAssigned] = useState(new Set());
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!file) return;
    try {
      const [org, mine] = await Promise.all([drive.listOrgLabels(), drive.listFileLabels(file.id)]);
      setAllLabels(org);
      setAssigned(new Set(mine.map((l) => l.id)));
    } catch (err) {
      setError(err.message || "Could not load labels.");
      setAllLabels([]);
    }
  }, [file]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName("");
    setAllLabels(null);
    load();
  }, [open, load]);

  const toggle = async (label) => {
    setBusy(true);
    setError(null);
    const isAssigned = assigned.has(label.id);
    try {
      if (isAssigned) await drive.unassignLabel(file.id, label.id);
      else await drive.assignLabel(file.id, label.id);
      setAssigned((prev) => {
        const next = new Set(prev);
        if (isAssigned) next.delete(label.id);
        else next.add(label.id);
        return next;
      });
    } catch (err) {
      setError(err.message || "Could not update that label.");
    } finally {
      setBusy(false);
    }
  };

  const createAndAssign = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const color = SWATCHES[(allLabels?.length || 0) % SWATCHES.length];
      const label = await drive.createLabel(name.trim(), color);
      await drive.assignLabel(file.id, label.id);
      setName("");
      await load();
    } catch (err) {
      setError(err.message || "Could not create that label.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Labels — {file?.name}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {allLabels === null ? (
          <Typography sx={{ fontSize: 13.5 }}>Loading…</Typography>
        ) : allLabels.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: "secondaryText.color", mb: 2 }}>
            No labels in this organisation yet — create the first one below.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {allLabels.map((label) => {
              const isAssigned = assigned.has(label.id);
              return (
                <Chip
                  key={label.id}
                  label={label.name}
                  size="small"
                  disabled={busy}
                  onClick={() => toggle(label)}
                  icon={isAssigned ? <CheckIcon sx={{ fontSize: 14 }} /> : undefined}
                  sx={{
                    fontSize: 12,
                    cursor: "pointer",
                    backgroundColor: isAssigned ? label.color : "transparent",
                    color: isAssigned ? "#fff" : "text.color",
                    border: `1px solid ${label.color}`,
                  }}
                />
              );
            })}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="New label"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ textTransform: "none" }}
            disabled={busy || !name.trim()}
            onClick={createAndAssign}
          >
            Add
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={TEXT_BUTTON_SX}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LabelsDialog;
